<template>
    <ui-container class="py-8 space-y-8" v-if="dailyBriefing">
        <!-- Header. -->
        <ui-header :title="`Daily Briefing ${dailyBriefing.formattedDate}`" :subtitle="'ID #'+this.dailyBriefing.id">
            <template #actions>
                <ui-dropdown placement="bottom-end" menu-class="p-3 w-64">
                    <template #default="{ toggle }">
                        <ui-button-new @click="toggle" primary>
                            Actions
                            <i class="flex-shrink-0 ml-2 fa fa-chevron-down"></i>
                        </ui-button-new>
                    </template>
                    <template #menu="{ close }">
                        <ui-dropdown-item icon="fas fa-calculator"
                                          @click="edit()">
                            Edit
                        </ui-dropdown-item>
                        <ui-dropdown-divider v-if="!dailyBriefing.locked"></ui-dropdown-divider>
                        <ui-dropdown-item v-if="!dailyBriefing.locked" danger icon="fa fa-trash"
                                          @click="openDeleteModal">Delete
                        </ui-dropdown-item>
                    </template>
                </ui-dropdown>
            </template>
        </ui-header>
        <ui-data-grid>
            <ui-data-grid-item label="Date" :value="dailyBriefing.formattedDate"></ui-data-grid-item>
            <ui-data-grid-item label="Location of Works" :value="dailyBriefing.locationOfWorks"></ui-data-grid-item>
            <ui-data-grid-item label="Key Activities" class="sm:col-span-full"
                               :value="dailyBriefing.keyActivities"></ui-data-grid-item>
            <ui-data-grid-item label="Key Hazards" class="sm:col-span-full"
                               :value="dailyBriefing.keyHazards"></ui-data-grid-item>
        </ui-data-grid>
        <div class="space-y-0">
            <label class="py-0">Attendees</label>
            <div class="flex mt-8 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                <div class="w-6 flex-shrink-0"></div>
                <div class="flex-1 border-l border-r border-gray-200 px-2 py-1">Name</div>
                <div class="flex-1 border-r border-gray-200 px-2 py-1">Position</div>
                <div class="w-20 border-r border-gray-200 px-2 py-1">Signature</div>
                <div class="w-6 flex-shrink-0 ml-2"></div>
            </div>
            <daily-briefings-attendee v-for="(attendee,index) in dailyBriefing.attendees"
                                      :key="attendee.id"
                                      :attendee="attendee"
                                      :daily-briefing="dailyBriefing"
                                      :contacts="contacts"
                                      :readonly="true"
                                      ref="attendees"
            ></daily-briefings-attendee>
        </div>
        <ui-data-grid>
            <ui-data-grid-item label="Given By" :value="dailyBriefing.givenByName"></ui-data-grid-item>
            <ui-data-grid-item label="Position" :value="dailyBriefing.givenByPosition"></ui-data-grid-item>
            <div class="lg:col-span-2">
                <dt class="text-sm font-medium text-gray-500">Signed</dt>
                <dd class="mt-1 text-sm text-gray-900 w-full">
                    <img v-if="dailyBriefing.signature" :src="dailyBriefing.signature" style="height:48px">
                    <div class="inline-block" v-else>
                        <ui-dropdown menu-class="p-3 w-56">
                            <template #default="{ toggle }">
                                <button @click="toggle"
                                        class="inline-block w-32 h-5 flex text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                                    <i class="fa fa-ellipsis-h fa-fw mt-0.5"></i>
                                    <span class="inline-block whitespace-pre-wrap text-red-600 ml-2">NOT Signed</span>
                                </button>
                            </template>
                            <template #menu="{ close }">
                                <ui-dropdown-item icon="fas fa-pencil" @click="addSignature();close();">Add Signature
                                </ui-dropdown-item>
                            </template>
                        </ui-dropdown>
                    </div>
                </dd>
            </div>
        </ui-data-grid>

        <send-log :send-log="combineSendLogs" @revoke="revoke" @resend="resend"></send-log>
        <ui-data-grid-item label="Quick action" wide>
            <div class="flex flex-col md:flex-row flex-wrap -m-2">
                <ui-button-new class="m-2" icon="fas fa-route" @click="duplicate" success>Duplicate
                </ui-button-new>
                <ui-button-new class="m-2" icon="fas fa-phone"
                               v-if="dailyBriefing.attendees.some((i)=> !i.signature && !i.signatureRequestedAt)"
                               @click="requestAllSignatures" extra>Request Signatures
                </ui-button-new>
                <ui-button-new class="m-2" icon="fas fa-file-pdf">
                    <a :href="dailyBriefing.getDownloadUrl()" tabindex="-1">Download PDF</a>
                </ui-button-new>
                <ui-button-new class="m-2" icon="fas fa-print" @click="print">Print PDF</ui-button-new>
                <ui-button-new class="m-2" icon="fas fa-route" extra
                               @click="send"
                >Send
                </ui-button-new>
            </div>
        </ui-data-grid-item>

        <ui-data-grid>
            <ui-data-grid-item label="Created By">{{ dailyBriefing.createdBy }} on {{
                    dailyBriefing.createdOn
                }}
            </ui-data-grid-item>
            <ui-data-grid-item label="Last Updated By" v-if="dailyBriefing.lastUpdatedBy">{{
                    dailyBriefing.lastUpdatedBy
                }} on {{
                    dailyBriefing.lastUpdatedOn
                }}
            </ui-data-grid-item>
        </ui-data-grid>

    </ui-container>
</template>

<script>
import AutoresizableTextarea from "../../ui/AutoresizableTextarea";
import UiContainer from "../../ui/UiContainer.vue";
import {DailyBriefing} from '@models'
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import UiDataGrid from "../../ui/UiDataGrid.vue";
import UiDataGridItem from "../../ui/UiDataGridItem.vue";
import UiHeader from "../../ui/UiHeader.vue";
import {uuid} from "../../../utils";
import SendLog from "../../send-log/SendLog.vue";
import moment from "moment";

export default {
    components: {
        SendLog,
        UiHeader,
        UiDataGridItem,
        UiDataGrid,
        UiButtonNew, UiDropdown, UiDropdownItem, UiContainer, AutoresizableTextarea
    },
    props: {
        item: Object,
        edit: Function,
        removeItem: Function,
        addItem: Function,
        contacts: Array(),
        packageReference: null
    },
    computed: {
        combineSendLogs() {
            return this.dailyBriefing.sendLog.map((a) => {
                return a;
            }).concat(...this.dailyBriefing.attendees.map((a) => a.sendLog ? a.sendLog.map((b) => {
                return b;
            }) : [])).sort((a, b) => moment(b.requested.at).unix() - moment(a.requested.at).unix())
        }
    },
    data() {
        return {
            dailyBriefing: null,
        }
    },
    watch: {
        dailyBriefing: {
            deep: true,
            handler(n, o) {
                Object.assign(this.item, this.dailyBriefing)
            },
        },
    },
    methods: {
        revoke(i) {
            this.dailyBriefing.revoke(i.id);
        },
        resend(i) {
            this.dailyBriefing.resend(i.id);
        },
        send() {
            this.$modals.open('daily-briefing-send-modal', {
                title: `Send Daily Briefing`,
                button: 'Send Daily Briefing',
                dailyBriefing: this.dailyBriefing,
                handle: async (daily, modal) => {
                    modal.close()
                }
            })
        },
        requestAllSignatures() {
            this.$modals.open('daily-briefing-multiple-signature-request', {
                title: `Send signature request to all attendees not yet requested`,
                contacts: this.contacts,
                attendees: this.dailyBriefing.attendees,
                handle: async (data, modal) => {
                    let requests = [];
                    data.forEach((i) => {
                        if (i.to) {
                            requests.push(this.dailyBriefing.requestSignature(i.id, i.to))
                        }
                    })
                    await Promise.all(requests).then(async () => {
                        let ndb = await DailyBriefing.find(this.dailyBriefing.packageReference, this.dailyBriefing.id)
                        Object.assign(this.dailyBriefing, ndb)
                        modal.close()
                        this.$nextTick(() => {
//                            window.Bus.emit('open-messaging');
                        })
                    })
                }
            })
        },
        addSignature() {
            this.$modals.sign({
                title: `Sign as "${this.dailyBriefing.givenByName}"`,
                button: 'Confirmed',
                handle: (data) => {
                    this.dailyBriefing.signature = data.signature;
                    this.dailyBriefing.update();
                }
            })
        },
        openDeleteModal() {
            const title = 'Are you sure you want to remove Daily Briefing ' + this.item.formattedDate + '?'
            const description = 'You are removing Daily Briefing #' + this.item.id + '. Are you sure you want to do this?'
            eventBus.$emit('delete-modal-open', title, description, async () => {
                await this.item.delete()
                this.removeItem(this.item)

            });
        },
        async duplicate() {
            let data = this.dailyBriefing.getFormAttributes();
            data.attendees = this.dailyBriefing.attendees ? this.dailyBriefing.attendees : [];
            data.attendees.map((i) => {
                i.id = uuid(), i.signature = null
            })
            data.signature = null
            data.id = uuid();
            const dailyBriefing = await DailyBriefing.create(this.packageReference, data.id, data);

            this.addItem(dailyBriefing, 0, (items) => {
                items.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
            });
            this.edit(dailyBriefing);
        },
        print() {
            this.$emit('print', this.item.getPrintableUrl())
        },
        async updateFromListener(data) {
            if (data.operation === 'updateAggregate') {
                if ((data.aggregate.type === 'DailyBriefing') && (data.aggregate.id === this.dailyBriefing.id)) {
                    Object.assign(this.dailyBriefing, data.aggregate.data)
                    Object.assign(this.item, data.aggregate.data)
                }
            }
        },
    },
    async mounted() {
        this.dailyBriefing = await DailyBriefing.find(this.item.packageReference, this.item.id)
        if (this.dailyBriefing) {
            User.registerAggregate({'type': 'DailyBriefing', 'id': this.dailyBriefing.id}, this.updateFromListener)
        }
    }

}
</script>
